import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import propertyImportExclusionsService from '../../service/api/PropertyImportExclusionsService';
import FormErrorMessage from '../_shared/FormErrorMessage';
import FormErrorMessageScroller from '../_shared/FormErrorMessageScroller';
import { useTranslation } from 'react-i18next';
import { PropertyImportExclusion } from '../../types/PropertyImportExclusion';

interface FormValuesProps extends PropertyImportExclusion {}

const INIT_FORM_STATE: FormValuesProps = {
  id: null,
  name: '',
};

type Props = {
  dialogVisible: boolean;
  closeAddEditModal: VoidFunction;
  propertyImportExclusion: PropertyImportExclusion;

  showAddToast: (name: string) => void;
  showSaveToast: (name: string) => void;
  readOnly?: boolean;
};

function PropertyImportExclusionDialog({
  dialogVisible,
  closeAddEditModal,
  propertyImportExclusion,

  showAddToast,
  showSaveToast,
  readOnly,
}: Props) {
  const [initFormValues, setInitFormValues] = useState(INIT_FORM_STATE);

  const { t } = useTranslation();

  const hideDialog = () => {
    formik.resetForm({ values: INIT_FORM_STATE });
    closeAddEditModal();
  };

  const editMode = !!propertyImportExclusion;
  useEffect(() => {
    if (dialogVisible && editMode) {
      setInitFormValues({
        id: propertyImportExclusion.id,
        name: propertyImportExclusion.name,
      });
    } else {
      setInitFormValues(INIT_FORM_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogVisible]);

  const formik = useFormik({
    initialValues: initFormValues,
    validate: (data: FormValuesProps) => {
      const errors: FormikErrors<FormValuesProps> = {};

      if (!data.name?.trim()) {
        errors.name = t('propertyImportExclusions.error_name_required');
      }
      return errors;
    },
    onSubmit: async (formData: FormValuesProps, helpers: FormikHelpers<FormValuesProps>) => {
      try {
        if (!formData.id) {
          await propertyImportExclusionsService.create(formData);
          showAddToast(formData.name);
        } else {
          await propertyImportExclusionsService.edit(formData, formData.id);
          showSaveToast(formData.name);
        }

        hideDialog();
      } catch (error: any) {
        if (error.response.status === 409) {
          const field = error.response.data.field;
          helpers.setFieldError(field, t(`propertyImportExclusions.error_${field}_already_taken`));
        }
      }
      helpers.setSubmitting(false);
    },
    enableReinitialize: true,
  });

  const DialogFooter = (
    <>
      <Button
        type="reset"
        label={t('common.cancel')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />

      <Button
        disabled={readOnly ? true : undefined}
        type="submit"
        label={t('common.save')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={formik.submitForm}
      />
    </>
  );

  return (
    <FormErrorMessageScroller formikInstance={formik}>
      <Dialog
        visible={dialogVisible}
        header={t('propertyImportExclusions.property_import_exclusion_details')}
        modal
        className="p-fluid"
        footer={DialogFooter}
        onHide={hideDialog}
        breakpoints={{ '1400px': '60vw', '896px': '90vw' }}
        style={{ width: '40vw' }}
      >
        <form>
          <div className="col-10">
            <div className="field">
              <label htmlFor="name">{t('propertyImportExclusions.name')}</label>
              <InputText
                id="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                disabled={readOnly ? true : undefined}
                className={formik.touched.name && formik.errors.name && 'p-invalid'}
              />
              <FormErrorMessage fieldName="name" formikInstance={formik} />
            </div>
          </div>
        </form>
      </Dialog>
    </FormErrorMessageScroller>
  );
}

export default PropertyImportExclusionDialog;
